import config from '@iotv/config';
import { MetaStatus } from '@iotv/iotv-v3-types';
import { createContext } from 'react';
export const GROUPING_PSEUDO_ROW_TYPE_ID = 'GroupingPseudoRow';
export const ROOT_SEQUENCE_ID = 'root';
export const ROOT_SEQUENCE_NAME = 'root';
const isRowObject = (x) => x &&
    typeof x.id === 'string' &&
    typeof x.sequenceId === 'string' &&
    typeof x.queryLevel === 'number' &&
    Array.isArray(x.queries) &&
    typeof x.queryExecuted === 'boolean' &&
    typeof x.visible === 'boolean';
export const defaultState = {
    rows: [],
};
const isAddRowsAction = (x) => x.type === 'ADD_ROWS' && Array.isArray(x.payload) && x.payload.every(isRowObject);
const isShowSubRowAction = (x) => x.type === 'SHOW_SUB_ROW' && isRowObject(x.payload);
const isHideSubRowAction = (x) => x.type === 'HIDE_SUB_ROW' && isRowObject(x.payload);
const isSetExecutedAction = (x) => x.type === 'SET_EXECUTED' && typeof x.payload === 'object' && typeof x.payload.sequenceId === 'string';
const isUpdateRowAction = (x) => x.type === 'UPDATE_ROW' && isRowObject(x.payload);
const isSetIsSmallViewAction = (x) => x.type === 'SET_IS_SMALL_VIEW' && typeof x.payload === 'boolean';
export const LazyQueryTableReducer = (state, action) => {
    const update = { ...state };
    let needs_sorting = false;
    if (isAddRowsAction(action)) {
        const { payload } = action;
        const existingSequenceIds = new Set(update.rows.map((row) => row.sequenceId));
        const existingIds = new Set(update.rows.map((row) => row.id));
        const newUniqueRows = [];
        for (const row of payload) {
            if (!existingSequenceIds.has(row.sequenceId) && !existingIds.has(row.id)) {
                const parentSequenceId = row.sequenceId.split('\u001e').slice(undefined, -1).join('\u001e');
                const parentIndex = update.rows.findIndex((r) => r.sequenceId === parentSequenceId);
                const parent = parentIndex !== -1 ? update.rows[parentIndex] : undefined;
                existingSequenceIds.add(row.sequenceId);
                existingIds.add(row.id);
                if (parent)
                    row.visible = parent.visible && parent.expanded;
                else
                    row.visible = false;
                if (row.type === GROUPING_PSEUDO_ROW_TYPE_ID) {
                    row[GROUPING_PSEUDO_ROW_TYPE_ID] = row.id;
                    row.visible = true;
                    row.expanded = false;
                }
                if (parent)
                    update.rows.splice(parentIndex + 1, 0, row);
                else
                    newUniqueRows.push(row);
            }
            needs_sorting = 1 < payload.length || 0 < newUniqueRows.length;
        }
        update.rows = [...update.rows, ...newUniqueRows];
    }
    else if (isHideSubRowAction(action)) {
        const { payload } = action;
        update.rows = update.rows.map((row) => {
            if (row.sequenceId === payload.sequenceId) {
                return { ...row, expanded: false };
            }
            else if (row.sequenceId.startsWith(payload.sequenceId)) {
                return { ...row, visible: false };
            }
            else {
                return row;
            }
        });
    }
    else if (isShowSubRowAction(action)) {
        const { payload } = action;
        const updated = [];
        for (const row of update.rows) {
            const parentSequenceId = row.sequenceId.split('\u001e').slice(undefined, -1).join('\u001e');
            const parent = updated.find((r) => r.sequenceId === parentSequenceId);
            if (row.sequenceId === payload.sequenceId) {
                updated.push({ ...row, expanded: true });
            }
            else if (row.sequenceId.startsWith(payload.sequenceId)) {
                updated.push({ ...row, visible: parent?.expanded ?? true });
            }
            else {
                updated.push(row);
            }
        }
        update.rows = updated;
    }
    else if (isSetExecutedAction(action)) {
        const { sequenceId } = action.payload;
        update.rows = update.rows.map((row) => {
            if (row.sequenceId === sequenceId) {
                return { ...row, querying: false, queryExecuted: true, expanded: true };
            }
            else {
                return row;
            }
        });
    }
    else if (isUpdateRowAction(action)) {
        const { payload } = action;
        update.rows = update.rows.map((row) => {
            if (row.id === payload.id && row.type === payload.type) {
                const parentSequenceId = row.sequenceId.split('\u001e').slice(undefined, -1).join('\u001e');
                const parent = update.rows.find((r) => r.sequenceId === parentSequenceId);
                if (parent)
                    payload.visible = parent.visible && parent.expanded;
                return { ...row, ...payload };
            }
            else {
                return row;
            }
        });
    }
    else if (isSetIsSmallViewAction(action)) {
        const { payload } = action;
        update.rows = update.rows.map((row) => ({ ...row, isSmallView: payload }));
    }
    /**
     * Custom-built and hand-tuned sorting algorithm, which works perfectly in all conditions
     *
     * *WARN: take extreme care before attempting to "improve" this code*
     *
     * `let hours_spent_sorting: number = 9`
     */
    const sorter = (a, b) => {
        // slice off the domain
        const [aDomainSk, ...aSequenceSks] = a.sequenceId.split('\u001e');
        const [bDomainSk, ...bSequenceSks] = b.sequenceId.split('\u001e');
        // slice off the domain
        const [aDomain, ...aSequenceNames] = a.sequenceName.split('\u001e');
        const [bDomain, ...bSequenceNames] = b.sequenceName.split('\u001e');
        // domain sorting is by config, not name
        const aDomainOrder = aDomain ? (config.apps?.webUI?.domainMap?.[aDomain]?.defaultSortOrder ?? 0) : 0;
        const bDomainOrder = bDomain ? (config.apps?.webUI?.domainMap?.[bDomain]?.defaultSortOrder ?? 0) : 0;
        // early return if in different domains
        const domain = aDomainOrder - bDomainOrder;
        if (domain !== 0)
            return domain;
        // if comparing to a domain
        const aIsDomain = a.sk?.includes(GROUPING_PSEUDO_ROW_TYPE_ID);
        const bIsDomain = b.sk?.includes(GROUPING_PSEUDO_ROW_TYPE_ID);
        // early return to sort domains to the top
        const isDomain = aIsDomain === bIsDomain ? 0 : aIsDomain ? -1 : 1;
        if (isDomain !== 0)
            return isDomain;
        // determine the common names
        const commonSequenceLength = Math.min(aSequenceNames.length, bSequenceNames.length);
        const aCommonNames = aSequenceNames.slice(0, commonSequenceLength);
        const bCommonNames = bSequenceNames.slice(0, commonSequenceLength);
        // for each level, compare
        for (let i = 0; i < commonSequenceLength; i++) {
            // compare by name
            const aCommonName = aCommonNames[i];
            const bCommonName = bCommonNames[i];
            // early return if names differ
            const order = aCommonName.localeCompare(bCommonName, undefined, { numeric: true, sensitivity: 'base' });
            if (order !== 0)
                return order;
            else {
                // fall back to cmparing the `sk`
                const aSk = aSequenceSks[i];
                const bSk = bSequenceSks[i];
                const sk = aSk.localeCompare(bSk);
                if (sk !== 0)
                    return sk;
            }
        }
        // they truly are of equal ordering
        return 0;
    };
    if (needs_sorting)
        update.rows.sort(sorter);
    // Populate pseudoDomain for the row with the first occerance of a Domain in its ancestry and null otherwise
    const postProcessed = postProcess(update);
    return postProcessed;
};
const postProcess = (mutableState) => {
    for (const row of mutableState.rows) {
        row[GROUPING_PSEUDO_ROW_TYPE_ID] = row.name;
        if (row.type === GROUPING_PSEUDO_ROW_TYPE_ID) {
            const children = mutableState.rows.filter((r) => row.sequenceId !== r.sequenceId && r.sequenceId.startsWith(row.sequenceId));
            if (!row.metadata)
                row.metadata = {};
            row.metadata.status = children.every((c) => (c.metadata?.status ? c.metadata?.status === MetaStatus.Normal : true))
                ? MetaStatus.Normal
                : MetaStatus.Abnormal;
        }
    }
    return mutableState;
};
export const LazyQueryTableContext = createContext(undefined);
